import { ValidId } from "@purposity/utils";
import * as z from "zod";

const teamJoin = z.object({
  id: ValidId,
});

const teamLeave = z.object({
  id: ValidId,
});

const teamInvite = z.object({
  id: ValidId,
});

const orgFollow = z.object({
  id: ValidId,
  orgName: z.string(),
  team: z.string().optional(),
});

const orgUnfollow = z.object({
  id: ValidId,
});

/** derived from CheckoutSession
 * @see packages/backend-sdk/src/features/transactions/types.ts
 * */
const transactionSchema = z.object({
  user_id: z.string(),
  checkout_intent_id: z.string().optional(),
  checkout_session_id: z.string().optional(),
});

const checkoutCreate = transactionSchema.partial();

const checkoutComplete = transactionSchema.partial();

const checkoutCancel = z.object({
  user_id: z.string(),
  checkout_intent_id: z.string(),
});

const checkoutExpire = z.object({
  user_id: z.string(),
  checkout_session_id: z.string(),
});

const goalSet = z.object({
  //
});

const userSignup = z.object({
  user_id: z.string(),
});

const userLogin = z.object({
  user_id: z.string(),
});

const userLogout = z.object({
  user_id: z.string(),
});

const needShare = z.object({
  needId: ValidId,
  needTitle: z.string(),
  user_id: z.string(),
  isUrgent: z.boolean().optional(),
  liveNeed: z.boolean().optional(),
});

const teamShare = z.object({
  teamId: ValidId,
  teamName: z.string(),
  user_id: z.string(),
});

const orgShare = z.object({
  orgId: ValidId,
  orgName: z.string(),
  user_id: z.string(),
});

const onboardingStart = z.object({
  //
});

const onboardingComplete = z.object({
  test: z.literal("me").optional(),
});

const storyClick = z.object({
  storyTitle: z.string(),
});

const followingTabClick = z.object({
  // Empty object since we don't need any additional properties
});

const needClick = z.object({
  needId: ValidId,
  needTitle: z.string(),
  isUrgent: z.boolean(),
  liveNeed: z.boolean(),
});

const profileButtonClick = z.object({
  // Empty object since we don't need any additional properties
});

const pageView = z.object({
  url: z.string(),
});

const sendgridEvent = z.object({
  user_id: z.string(),
  campaign_id: z.number(),
  campaign_name: z.string(),
  single_send_id: z.string(),
  event_type: z.string(),
  event_timestamp: z.string(),
  need_id: z.number().optional(),
});

export const EventMap = z.object({
  "Page View": pageView,
  "Team Join": teamJoin,
  "Team Leave": teamLeave,
  "Team Invite": teamInvite,
  "Organization Follow": orgFollow,
  "Organization Unfollow": orgUnfollow,
  "Goal Set": goalSet,
  "User Signup": userSignup,
  "User Login": userLogin,
  "User Logout": userLogout,
  "Checkout Create": checkoutCreate,
  "Checkout Complete": checkoutComplete,
  "Checkout Cancel": checkoutCancel,
  "Checkout Expire": checkoutExpire,
  "Need Share": needShare,
  "Team Share": teamShare,
  "Organization Share": orgShare,
  "Onboarding Start": onboardingStart,
  "Onboarding Complete": onboardingComplete,
  "Story View": storyClick,
  "Following Tab Click": followingTabClick,
  "Need Click": needClick,
  "Profile Click": profileButtonClick,
  "Email Opened": sendgridEvent,
  "Email Clicked": sendgridEvent,
  "Email Delivered": sendgridEvent,
  "Email Bounced": sendgridEvent,
  "Email Deferred": sendgridEvent,
  "Email Spam Reported": sendgridEvent,
  "Email Unsubscribed": sendgridEvent,
});
export type EventMap = z.infer<typeof EventMap>;

export type Options = {
  eventId?: string;
};

/**
 * Returns a specific event name for a page view based on the URL pattern.
 */
export function getPageViewEventName(url: string): string {
  // Normalize URL: remove query params/hash and trailing slash (except root)
  url = url.split("?")?.[0]?.split("#")?.[0] ?? "";
  if (url.length > 1 && url.endsWith("/")) url = url.slice(0, -1);
  if (/^\/organization\/[^/]+$/.test(url)) {
    return "Organization Page View";
  }
  if (/^\/team\/[^/]+$/.test(url)) {
    return "Team Page View";
  }
  if (url === "/teams") {
    return "Teams Page View";
  }
  if (url === "/organizations") {
    return "Organizations Page View";
  }
  if (url === "/stories") {
    return "Stories Page View";
  }
  if (url.startsWith("/profile/")) {
    return "Profile Page View";
  }
  if (url === "/logout") {
    return "Logout Page View";
  }
  if (url === "/organization/givemore") {
    return "Give More Page View";
  }
  if (/^\/organization\/[^/]+\/give$/.test(url)) {
    return "Organization Give More Page View";
  }
  if (/^\/need\/[^/]+$/.test(url)) {
    return "Need Page View";
  }
  if (url.startsWith("/home")) {
    return "Home Page View";
  }
  if (url.startsWith("/login")) {
    return "Login Page View";
  }
  if (/^\/new-page-view\/.+$/.test(url)) {
    return "New Page View";
  }
  // Add more rules as needed
  return "Page View";
}
